import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { portalGetSubscriptionMetrics } from '../../lib/portalApi';
import { auth } from '../../lib/firebase';

export function DashboardView({ appId, tenantId, env }: { appId: string, tenantId: string, env: string }) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setError(null);
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) throw new Error('Not authenticated');
        const data = await portalGetSubscriptionMetrics({ tenantId, appId, env, idToken, period: 'month' });
        setHistory(data.history || []);
      } catch (err: any) {
        console.error('Failed to fetch metrics', err);
        setError(err.message || 'Failed to fetch metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [tenantId, appId, env]);

  if (loading) {
    return <div className="text-on-surface p-xl">Loading Dashboard...</div>;
  }

  if (error) {
    return <div className="p-md bg-error-container text-on-error-container rounded-lg border border-error/20 font-body-sm mb-md">{error}</div>;
  }

  const currentMonth = history[history.length - 1];
  const lastMonth = history.length > 1 ? history[history.length - 2] : null;

  const currentMRR = currentMonth?.metrics?.mrr ?? 0;
  const lastMRR = lastMonth?.metrics?.mrr ?? 0;
  const mrrGrowth = lastMRR > 0 ? ((currentMRR - lastMRR) / lastMRR) * 100 : (currentMRR > 0 ? 100 : 0);

  const currentSubs = currentMonth?.metrics?.active?.all ?? 0;
  const lastSubs = lastMonth?.metrics?.active?.all ?? 0;
  const subsGrowth = lastSubs > 0 ? ((currentSubs - lastSubs) / lastSubs) * 100 : (currentSubs > 0 ? 100 : 0);

  // Backend trả về SỐ LƯỢNG huỷ/hết hạn/hoàn tiền trong kỳ, không phải tỷ lệ.
  // Tỷ lệ = số rời đi / (số rời đi + số còn lại) — tức phần trăm của những
  // người từng hoạt động trong kỳ mà tới giờ đã rời.
  const churnCount = currentMonth?.metrics?.churn?.all ?? 0;
  const churnBase = churnCount + currentSubs;
  const churnRate = churnBase > 0 ? (churnCount / churnBase) * 100 : 0;

  const formatGrowth = (val: number) => {
    if (val > 0) return `+${val.toFixed(1)}% so với tháng trước`;
    if (val < 0) return `${val.toFixed(1)}% so với tháng trước`;
    return 'Không đổi so với tháng trước';
  };

  const mrrGrowthColor = mrrGrowth >= 0 ? 'text-[#15803d]' : 'text-error';
  const subsGrowthColor = subsGrowth >= 0 ? 'text-primary' : 'text-error';

  const chartRevenueData = history.map((h: any) => ({
    name: h.label,
    mrr: h.metrics?.mrr ?? 0
  }));

  // Biểu đồ này có tiêu đề "Người đăng ký mới" nên phải vẽ `new`, không phải
  // `active` — trước đây nó vẽ tổng số người đang hoạt động.
  const chartSubscribersData = history.map((h: any) => ({
    name: h.label,
    subs: h.metrics?.new?.all ?? 0
  }));

  return (
    <div className="flex flex-col gap-lg">
      
      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-md md:gap-lg">
        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h3 className="m-0 font-label-md text-[14px] text-on-surface-variant">Doanh thu MRR (Tháng này)</h3>
          <div className={`font-headline-lg text-[28px] md:text-[40px] font-bold mt-2 ${currentMRR >= 0 ? 'text-[#15803d]' : 'text-on-surface'}`}>
            ${currentMRR.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </div>
          <div className={`font-label-sm text-[12px] mt-2 ${mrrGrowthColor}`}>
            {formatGrowth(mrrGrowth)}
          </div>
        </div>
        
        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h3 className="m-0 font-label-md text-[14px] text-on-surface-variant">Active Subscribers</h3>
          <div className="font-headline-lg text-[28px] md:text-[40px] font-bold text-primary mt-2">
            {currentSubs.toLocaleString()}
          </div>
          <div className={`font-label-sm text-[12px] mt-2 ${subsGrowthColor}`}>
            {formatGrowth(subsGrowth)}
          </div>
        </div>

        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h3 className="m-0 font-label-md text-[14px] text-on-surface-variant">Tỷ lệ rời bỏ (Churn)</h3>
          <div className={`font-headline-lg text-[28px] md:text-[40px] font-bold mt-2 ${churnRate > 0 ? 'text-error' : 'text-on-surface'}`}>
            {churnRate.toFixed(1)}%
          </div>
          <div className="font-label-sm text-[12px] mt-2 text-on-surface-variant">
            {churnCount} rời đi / {churnBase} từng hoạt động trong tháng
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-lg">
        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h2 className="font-headline-md text-[20px] font-semibold text-on-surface mb-lg">Biểu đồ MRR (Monthly Recurring Revenue)</h2>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartRevenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorMrr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#15803d" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#15803d" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#737686" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e3e5" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#191c1e', border: 'none', borderRadius: '8px', color: '#ffffff', fontSize: '12px' }}
                  itemStyle={{ color: '#15803d' }}
                />
                <Area type="monotone" dataKey="mrr" stroke="#15803d" strokeWidth={3} fillOpacity={1} fill="url(#colorMrr)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-surface-container-lowest border border-outline-variant p-lg rounded-xl shadow-sm">
          <h2 className="font-headline-md text-[20px] font-semibold text-on-surface mb-lg">Người đăng ký mới (New Subscribers)</h2>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartSubscribersData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#737686" fontSize={12} tickLine={false} axisLine={false} />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e3e5" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#191c1e', border: 'none', borderRadius: '8px', color: '#ffffff', fontSize: '12px' }}
                  itemStyle={{ color: '#004ac6' }}
                  cursor={{ fill: '#f2f4f6' }}
                />
                <Bar dataKey="subs" fill="#004ac6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
}
